import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  BaseStitchListComponent,
  type StitchListItem,
} from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { Like } from '@modules/common/types/like';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { TaskComponent } from '@modules/elements/components/task/task.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { isStitchBoundaryFloating } from '@modules/tasks/utils/stitch-time.util';
import { normalizeAllDayDate } from '@sharedlib/common/utils/date';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-tasks-list',
  templateUrl: './tasks-list.component.html',
  styleUrls: ['./tasks-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class TasksListComponent extends BaseStitchListComponent<Task> implements OnInit, OnDestroy, OnChanges {
  /** Show per-row project-location badges (search results, where results span projects). */
  public readonly showLocationBadge = input<boolean>(false);

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Owner carrier for embedded item-owned lists; ordinary app lists keep their selection. */
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** The main task window closes removed tasks; related-item hosts can open the next selection. */
  public readonly openOnRemoval = input(true);
  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  public items: StitchListItem<Task>[] = [];

  @Input()
  public override filters: Like<TasksFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickTask = new EventEmitter<Task>();
  @Output()
  public readonly openTask = new EventEmitter<Task>();
  @Output()
  public readonly doubleClickTask = new EventEmitter<Task>();
  @Output()
  public readonly openSubtask = new EventEmitter<Task>();

  @ViewChildren(TaskComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  private readonly tasksService = inject(TasksService);
  private readonly tasksStateService = inject(TaskingAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    return [
      ...new Set([
        ...super.stitchSignalSpaceIds(),
        ...(this.tenantItems() ?? []).flatMap(({ accountId }) => (accountId ? [accountId] : [])),
      ]),
    ];
  }

  protected override getStitchType(): StitchType {
    return StitchType.TASK;
  }

  public override ngOnInit() {
    merge(
      // Global refresh button
      this.tasksStateService.getRefreshAll(),
      // Subscribe for linked info updates
      this.linkedInfoService.getRefresh(),
      this.tasksService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes || 'tenantItems' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Task[]; count: number }> {
    return this.tasksService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
        // Top-level tasks only by default — a task list browses one level, so subtasks surface under
        // their project rather than beside it. An explicit `noParent` from the caller WINS, though:
        // a search surface (the Knowledge Panel results list) spans every container by nature, and
        // hard-forcing `true` here hid every in-project task from it. The backend drops its own
        // orphan-only default for text/marker searches (see isSearchApplied) but cannot know that a
        // date-only request is a search, so the caller has to say so.
        // Trash is the exception (#1610): a trashed subtask is listed there, under its parent's name.
        noParent: this.filters?.hubMode
          ? undefined
          : (this.filters?.noParent ?? (this.filters?.deleted ? undefined : true)),
      },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
      },
    ).pipe(switchMap((response) => this.withParentTitles(response)));
  }

  /** Trash's subtask rows name their parent task (#1610). */
  protected readonly parentTitles = signal(new Map<string, string>());

  /** One bulk uids[] read per Trash page for the parents its subtasks name; a failed read leaves the rows as they are. */
  private withParentTitles<R extends { items: Task[] }>(response: R): Observable<R> {
    const parents = this.filters?.deleted
      ? [
          ...new Map(
            response.items
              .filter((task) => !!task.parentId)
              .map((task) => [task.parentId, { id: task.parentId, accountId: task.accountId }] as const),
          ).values(),
        ]
      : [];
    if (!parents.length) return of(response);
    return this.tasksService.getItemsBulk(parents, true).pipe(
      map((found) => {
        this.parentTitles.update(
          (titles) => new Map([...titles, ...found.map((parent): [string, string] => [parent.id, parent.title])]),
        );
        return response;
      }),
      catchError(() => of(response)),
    );
  }

  /** The parent task's name on a subtask row in Trash; a parent it cannot name reads as an untitled task. */
  protected parentName(task: Task): string | undefined {
    if (!this.filters?.deleted || !task.parentId) return undefined;
    return this.parentTitles().get(task.parentId) || 'Untitled Task';
  }

  public handleDoubleClickItem(task: Task) {
    this.doubleClickTask.emit(task);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Task>): void {
    // Removing a selected task may advance the list's keyboard selection, but that is not a
    // request to replace the task window. Its next menu action must never reach the neighbour.
    if (event.source === 'removal' && !this.openOnRemoval()) return;
    this.clickTask.emit(event.item.data);
  }

  protected override getSectionValue(item: Task): unknown | undefined {
    if (this.filters.sortBy === 'priority') {
      return item[this.filters.sortBy];
    }
    if (this.filters.sortBy === 'date') {
      return item.startDateTime ? this.getDateSectionValue(item, item.startDateTime) : undefined;
    }
    if (this.filters.sortBy === 'due-date') {
      return item.endDateTime ? this.getDateSectionValue(item, item.endDateTime) : undefined;
    }
    return super.getSectionValue(item);
  }

  /**
   * SBUX-829: a FLOATING boundary is a timezone-agnostic calendar date stored at UTC midnight — read
   * its stored day directly instead of projecting it, or an account west of UTC files it a day early.
   * A TIMED boundary is a real instant and takes the base projection.
   *
   * Floating-ness is a property of the FIELD being grouped, not of the item: sorting by "Created"
   * groups on `createdAt`, which is always a real instant even for a task with no start time. Deciding
   * it from the start boundary normalized that instant as if it were floating, so the heading named
   * the instant's UTC day — a task created at 02:23 Kyiv landed under "Yesterday" while its own row
   * read Jul 31. Reproduced live.
   */
  protected override getDateSectionValue(item: Task, value: Date): Date {
    const boundary = this.sectionBoundary();
    return boundary && isStitchBoundaryFloating(item, boundary)
      ? normalizeAllDayDate(value)
      : super.getDateSectionValue(item, value);
  }

  /**
   * Which item boundary the current sort field reads, or `null` when the field is a real instant
   * (`createdAt`) and therefore can never be floating. The remaining sort fields — `title`,
   * `priority`, `position`, `issue-key`, `score` — never yield a `Date`, so they never get here.
   */
  private sectionBoundary(): 'start' | 'end' | null {
    if (this.filters.sortBy === 'date') {
      return 'start';
    }
    if (this.filters.sortBy === 'due-date') {
      return 'end';
    }
    return null;
  }
}
